import { getUrlTitle, toURL } from "@prismicio/editor-support/URL"
import { ErrorBoundary } from "@prismicio/editor-ui"
import {
  FilledFileLinkContent,
  FilledImageLinkContent,
} from "@prismicio/types-internal/lib/content"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import type { Editor } from "@tiptap/core"
import { Suspense } from "react"

import { useEditorConfig } from "../../../../EditorConfig"
import { LinkDescriptionCard, LinkImageCard } from "../../../LinkField"
import { parseMediaLinkData } from "../../extensions/Link/Link"
import type { LinkElementData } from "../../extensions/Link/LinkModel"
import { createRunCommandsFn } from "../../models/MenuItems"
import { DocumentDescriptionCard } from "./DocumentDescriptionCard"

interface LinkPopoverProps {
  editor: Editor
  onEditWebLink: () => void
  onEditDocumentLink: () => void
  onEditMediaLink: () => void
}

export function LinkPopover(props: LinkPopoverProps) {
  const { editor } = props

  const { searchDocuments } = useEditorConfig()

  const linkAttributes = editor.getAttributes(RichTextNodeType.hyperlink)

  if (linkAttributes["id"] && searchDocuments) {
    const docId = linkAttributes["id"] as string
    return <DocumentLinkPopover docId={docId} {...props} />
  }

  if (linkAttributes["href"]) {
    const url = linkAttributes["href"] as string
    return <WebLinkPopover url={url} {...props} />
  }

  if (linkAttributes["media"]) {
    const media = parseMediaLinkData(linkAttributes["media"] as string)
    return <MediaLinkPopover media={media} {...props} />
  }

  return null
}
interface DocumentLinkPopoverProps {
  editor: Editor
  onEditDocumentLink: () => void
  docId: string
}

export function DocumentLinkPopover(props: DocumentLinkPopoverProps) {
  const { editor, onEditDocumentLink, docId } = props
  return (
    <ErrorBoundary
      renderError={() => (
        <LinkDescriptionCard
          title="Failed to Load Linked Document Data"
          error
          onEdit={onEditDocumentLink}
          onUnlink={() => unsetLink(editor)}
        />
      )}
    >
      <Suspense
        fallback={
          <LinkDescriptionCard
            loading
            onEdit={onEditDocumentLink}
            onUnlink={() => unsetLink(editor)}
          />
        }
      >
        <DocumentDescriptionCard
          docId={docId}
          onEdit={onEditDocumentLink}
          onUnlink={() => unsetLink(editor)}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

interface WebLinkPopoverProps {
  editor: Editor
  onEditWebLink: () => void
  url: string
}

function WebLinkPopover(props: WebLinkPopoverProps) {
  const { url, editor, onEditWebLink } = props
  const formattedUrl = toURL(url)
  if (formattedUrl) {
    return (
      <LinkDescriptionCard
        url={url}
        title={getUrlTitle(formattedUrl)}
        subTitle={url}
        onEdit={onEditWebLink}
        onUnlink={() => unsetLink(editor)}
      />
    )
  }
  return (
    <LinkDescriptionCard
      url={url}
      subTitle={url}
      onEdit={onEditWebLink}
      onUnlink={() => unsetLink(editor)}
    />
  )
}

interface MediaLinkPopoverProps {
  editor: Editor
  onEditMediaLink: () => void
  media: LinkElementData
}

function MediaLinkPopover(props: MediaLinkPopoverProps) {
  const { media, editor, onEditMediaLink } = props
  if (FilledImageLinkContent.is(media)) {
    return (
      <LinkImageCard
        url={media.url}
        title={media.name}
        subTitle={`${media.width} x ${media.height} pixels`}
        onEdit={onEditMediaLink}
        onUnlink={() => unsetLink(editor)}
      />
    )
  }

  if (FilledFileLinkContent.is(media)) {
    return (
      <LinkDescriptionCard
        url={media.url}
        title={media.name}
        subTitle={getExtensionFromFilename({ filename: media.name })}
        largeIconName="insertDriveFile"
        onEdit={onEditMediaLink}
        onUnlink={() => unsetLink(editor)}
      />
    )
  }

  return null
}

function unsetLink(editor: Editor) {
  const setLink = createRunCommandsFn((commands) => commands.unsetLink())
  setLink(editor)
}

interface GetExtensionFromFilenameArgs {
  filename: string
}

function getExtensionFromFilename(args: GetExtensionFromFilenameArgs) {
  const { filename } = args
  const extension = filename.split(".").pop()

  if (extension) return extension.toLowerCase()
  return ""
}
